import { Box, Paper, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import * as services from "../../services/saleng";
import toast, { Toaster } from "react-hot-toast";
import AppBreadcrumbs from "./../../components/AppBreadcrumbs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { green, red } from "@mui/material/colors";
import { formatNumber } from "./../../utils/formatNumber";
import { formatDate } from "../../utils/formatDate";

const Page = () => {
  const { branchId } = useParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowModesModel, setRowModesModel] = useState({});

  const columns = [
    { field: "no", headerName: "ลำดับ", flex: 0, width: 70 },
    // {
    //   field: "image",
    //   headerName: "รูปภาพ",
    //   flex: 0,
    //   width: 100,
    //   renderCell: (params) => {
    //     if (params.value) {
    //       return (
    //         <img
    //           src={params.value}
    //           style={{ width: "50px", height: "50px" }}
    //           alt=""
    //         />
    //       );
    //     }
    //   },
    // },
    { field: "title", headerName: "ชื่อสินค้า", flex: 2 },
    { field: "category", headerName: "หมวดหมู่", flex: 1 },
    { field: "subCategory", headerName: "หมวดหมู่ย่อย", flex: 1 },
    { field: "description", headerName: "คำอธิบาย", flex: 1.5 },
    {
      field: "visible",
      headerName: "แสดงผล",
      width: 120,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const isVisible = params.value;
        const color = isVisible ? green[600] : red[500];
        const Icon = isVisible ? CheckCircleIcon : CancelIcon;

        return (
          <Icon
            style={{ color, cursor: "pointer" }}
            onClick={() => handleToggleVisible(params.row.id, !isVisible)}
          />
        );
      },
    },
    {
      field: "price",
      headerName: "ราคาซื้อทั่วไป",
      type: "number",
      flex: 1.5,
      minWidth: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const value = params.value ?? 0;
        return (
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            alignItems={"center"}
            width={"100%"}
            gap={4}
          >
            {formatNumber(value)}
          </Box>
        );
      },
    },
    {
      field: "adjustment",
      headerName: "ราคาเพิ่ม/ลด ต่อหน่วย",
      type: "number",
      editable: true,
      flex: 1.5,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const value = params.value ?? 0;
        const color = value < 0 ? "#f44336" : "#4caf50";
        return (
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            alignItems={"center"}
            width={"100%"}
            gap={4}
            color={color}
          >
            {formatNumber(value)}
          </Box>
        );
      },
    },
    {
      field: "total",
      headerName: "ราคารวม",
      type: "number",
      flex: 1.5,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const value = params.value;
        return (
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            alignItems={"center"}
            width={"100%"}
            gap={4}
          >
            {formatNumber(value)}
          </Box>
        );
      },
    },
    {
      field: "updatedAt",
      headerName: "แก้ไขล่าสุด",
      flex: 2,
      renderCell: (params) => formatDate(params.value),
    },

    // {
    //   field: "description",
    //   headerName: "ข้อมูลเพิ่มเติม",
    //   flex: 0,
    //   sortable: false,
    //   filterable: false,
    //   disableColumnMenu: true,
    //   renderCell: (params) => {
    //     console.log("params", params);
    //     return (
    //       <SubjectIcon
    //         onClick={() => window.open("/reports/" + params.row.transactionId)}
    //       />
    //     );
    //   },
    // },
  ];

  const handleToggleVisible = async (productId, newVisible) => {
    const row = rows.find((r) => r.id === productId);
    if (!row) return;

    const payload = {
      branchId,
      productId,
      visible: newVisible,
    };

    try {
      await services.updateProductSetting(payload, productId);

      // อัปเดตใน DataGrid
      setRows((prev) =>
        prev.map((r) =>
          r.id === productId
            ? {
                ...r,
                visible: newVisible,
                settingItem: true,
              }
            : r
        )
      );

      toast.success("อัปเดตการแสดงผลแล้ว");
    } catch (err) {
      toast.error("อัปเดตการแสดงผลล้มเหลว");
    }
  };

  const fetchProducts = useCallback(async () => {
    const res = await services.getProducts();
    const products = res.data.data;

    const adjRes = await services.getProductAdjustments(branchId);
    const adjustments = adjRes.data.data;

    const settingRes = await services.getProductSettings(branchId);
    const settings = settingRes.data.data;

    const settingMap = new Map(settings.map((s) => [s.productId, s]));

    const productsWithIndex = products.map((item, index) => {
      const adjItem = adjustments.find((a) => a.productId === item.id);
      const setting = settingMap.get(item.id);

      const adjustment = adjItem?.adjustment ?? 0;

      return {
        ...item,
        no: index + 1,
        category: item.category.name,
        subCategory: item.subCategory.name,
        adjustment,
        total: (item.price ?? 0) + adjustment,
        adjItem: !!adjItem,
        visible: setting?.visible ?? true,
      };
    });

    setRows(productsWithIndex);
    setLoading(false);
  }, [branchId]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const processRowUpdate = async (newRow) => {
    newRow.total = Number(newRow.price) + Number(newRow.adjustment);

    const payload = {
      productId: newRow.id,
      adjustment: newRow.adjustment,
    };

    try {
      if (newRow.adjItem) {
        // มี adjustment อยู่แล้ว → ทำการ update
        await services.updateProductAdjustments(payload, branchId);
        console.log("🔄 update");
      } else {
        // ยังไม่มี adjustment → สร้างใหม่
        await services.createProductAdjustments(payload, branchId);
        console.log("✨ create");
        payload.adjItem = true;
      }

      toast.success("อัปเดตราคาสำเร็จแล้ว");
      return { ...newRow, isNew: false };
    } catch (error) {
      console.error("Error updating row:", error);
      toast.error("เกิดข้อผิดพลาดในการอัปเดตราคา");
      throw error;
    }
  };

  return (
    <div>
      <AppBreadcrumbs />
      <Toaster position="bottom-left" reverseOrder={false} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 2,
        }}
      >
        <Typography component="h2" variant="h5">
          จัดการราคา
        </Typography>
      </Box>
      <Box sx={{ flex: 1, position: "relative" }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <Paper>
            <DataGrid
              rows={rows}
              columns={columns}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={setRowModesModel}
              processRowUpdate={processRowUpdate}
              loading={loading}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 25 } },
              }}
            />
          </Paper>
        </Box>
      </Box>
    </div>
  );
};

export default Page;
